/**
 * =============================================================================
 * FASIT — alle tidskonstanter i millisekunder (unntatt maks forsøk o.l.)
 * Juster her før deploy. Backend: `backend/config/timeouts.js` — hold
 * `ACCESS_TOKEN_MS` og `SESSION_INACTIVITY_LIMIT_MS` i synk.
 * =============================================================================
 */

/** Access JWT / cookie — må matche backend `ACCESS_TOKEN_MS` */
const ACCESS_TOKEN_MS = 1000 * 60 * 15;

/** Inaktivitet uten vellykket refresh — må matche backend `SESSION_INACTIVITY_LIMIT_MS` */
const SESSION_INACTIVITY_LIMIT_MS = 1000 * 60 * 60 * 12;

/** Varsel (SweetAlert) før inaktiv utlogging */
const SESSION_IDLE_WARNING_BEFORE_EXPIRY_MS = 1000 * 60 * 60 * 1;

/** Intervall for klient-sjekk av idle / varsel */
const SESSION_WARNING_TICK_MS = 1000 * 4;

/**
 * Periodisk access-refresh mens brukeren er aktiv.
 * Sett `TOKEN_REFRESH_DISABLE_PERIODIC_INTERVAL` til `true` hvis intervall ville
 * holde i live en kort test-økt uten reell brukeraktivitet (samme rolle som gammel «kort test»).
 */
const TOKEN_REFRESH_DISABLE_PERIODIC_INTERVAL = false;
const TOKEN_REFRESH_INTERVAL_MS = 1000 * 60 * 10;

/** Maks tid uten muse/tastatur før vi ikke refresher på intervall */
const TOKEN_REFRESH_ACTIVITY_THRESHOLD_MS = ACCESS_TOKEN_MS;

/** Synlig-fane refresh: minimum tid mellom kall */
const VISIBILITY_REFRESH_THROTTLE_MS = 1000 * 30;

/** P1-6: refresh-retry */
const AUTH_REFRESH_MAX_ATTEMPTS = 3;
const AUTH_REFRESH_BACKOFF_BASE_MS = 400;

/** Innlogging: animasjon + redirect-forsinkelse */
const LOGIN_REDIRECT_ANIMATION_INTERVAL_MS = 500;
const LOGIN_REDIRECT_DELAY_MS = 1000 * 4;

/** MailServerWarning */
const MAIL_HEALTH_CHECK_INTERVAL_MS = 1000 * 60 * 2;

/** Header: nedtelling */
const HEADER_SESSION_TICK_MS = 1000;

/** RegisterForm */
const REGISTER_SUCCESS_RESET_MS = 1000 * 3;

/** useTokenRefresh: scroll-logg throttling */
const AUTH_ACTIVITY_SCROLL_LOG_THROTTLE_MS = 1000 * 3;

/** Debounce før serverlagring av skjema-utkast (PUT /form/draft/me) */
const FORM_AUTOSAVE_DEBOUNCE_MS = 2500;

/** Timeout for AI-forslag til målretting (POST /form/ai-suggest-targeting) */
const FORM_AI_SUGGEST_TIMEOUT_MS = 1000 * 120;

// --- Ikke-timer / logging (styres herfra, ikke .env) ---
/** `true` = hyppig `[auth/idle-check]` hvert `SESSION_WARNING_TICK_MS` også i dev */
const AUTH_IDLE_CLIENT_LOG_FREQUENT = false;

/** `true` = alltid utførlig refresh-logg i AuthContext */
const AUTH_REFRESH_CLIENT_LOG_VERBOSE = false;

/** `false` = ingen automatisk refresh-logg i utvikling (kun hvis VERBOSE over er true) */
const AUTH_REFRESH_CLIENT_LOG_IN_DEV = true;

/** `true` = logg hver aktivitetshendelse i useTokenRefresh (scroll throttlet) */
const AUTH_ACTIVITY_CLIENT_VERBOSE = false;

const MINUTE_MS = 60 * 1000;

export const accessTokenMinutes = Math.round(ACCESS_TOKEN_MS / MINUTE_MS);
export const sessionInactivityTimeoutMinutes = Math.ceil(
	SESSION_INACTIVITY_LIMIT_MS / MINUTE_MS
);
export const sessionInactivityTimeoutHours =
	sessionInactivityTimeoutMinutes / 60;

export const TIMEOUTS = {
	accessTokenMinutes,
	sessionInactivityTimeoutHours,
	sessionInactivityTimeoutMinutes,

	sessionIdleWarningMsBeforeExpiry: SESSION_IDLE_WARNING_BEFORE_EXPIRY_MS,
	sessionWarningTickMs: SESSION_WARNING_TICK_MS,

	tokenRefreshIntervalMs: TOKEN_REFRESH_INTERVAL_MS,
	tokenRefreshActivityThresholdMs: TOKEN_REFRESH_ACTIVITY_THRESHOLD_MS,
	visibilityRefreshThrottleMs: VISIBILITY_REFRESH_THROTTLE_MS,

	/** Når `true`: ingen `setInterval`-refresh (kun visibility + API/401). */
	tokenRefreshPeriodicDisabled: TOKEN_REFRESH_DISABLE_PERIODIC_INTERVAL,

	authRefreshMaxAttempts: AUTH_REFRESH_MAX_ATTEMPTS,
	authRefreshBackoffBaseMs: AUTH_REFRESH_BACKOFF_BASE_MS,

	loginRedirectAnimationIntervalMs: LOGIN_REDIRECT_ANIMATION_INTERVAL_MS,
	loginRedirectDelayMs: LOGIN_REDIRECT_DELAY_MS,

	mailHealthCheckIntervalMs: MAIL_HEALTH_CHECK_INTERVAL_MS,
	headerSessionTickMs: HEADER_SESSION_TICK_MS,
	registerSuccessResetMs: REGISTER_SUCCESS_RESET_MS,

	authActivityScrollLogThrottleMs: AUTH_ACTIVITY_SCROLL_LOG_THROTTLE_MS,

	formAutosaveDebounceMs: FORM_AUTOSAVE_DEBOUNCE_MS,
	formAiSuggestTimeoutMs: FORM_AI_SUGGEST_TIMEOUT_MS,
};

export const authInactiveIdleCheckLogging =
	process.env.NODE_ENV === 'development' || AUTH_IDLE_CLIENT_LOG_FREQUENT;

export const authInactiveIdleCheckLoggingFrequent =
	AUTH_IDLE_CLIENT_LOG_FREQUENT;

export const authRefreshClientVerboseLogging =
	AUTH_REFRESH_CLIENT_LOG_VERBOSE ||
	(process.env.NODE_ENV === 'development' && AUTH_REFRESH_CLIENT_LOG_IN_DEV);

/** Ekstra aktivitetslogging i `useTokenRefresh` */
export const authActivityVerboseLogging =
	AUTH_ACTIVITY_CLIENT_VERBOSE ||
	process.env.NODE_ENV === 'development';

export const getSessionIdleConfigDevLogPayload = () => ({
	ACCESS_TOKEN_MS,
	SESSION_INACTIVITY_LIMIT_MS,
	SESSION_IDLE_WARNING_BEFORE_EXPIRY_MS,
	SESSION_WARNING_TICK_MS,
	TOKEN_REFRESH_INTERVAL_MS,
	TOKEN_REFRESH_DISABLE_PERIODIC_INTERVAL,
	sessionInactivityTimeoutMinutes: TIMEOUTS.sessionInactivityTimeoutMinutes,
	sessionIdleWarningMsBeforeExpiry: TIMEOUTS.sessionIdleWarningMsBeforeExpiry,
});
